import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import type { FreelancerProjectHistoryRow } from "@/lib/db-types";

/**
 * VIB-2927: prosjekthistorikk fra RPC-en freelancer_project_history. Utledes av
 * frilanser på eksterne linjer og bilag fra koblede leverandører. «Fakturert»
 * er et visningstall (delmengde av GEEK-kostnaden), ikke en KPI.
 */

/** Frilans på prosjekt krever både sitt eget flagg og frilanserbanken (lenkene går dit). */
export function useFreelancerProjectsEnabled(): boolean {
  const projects = useFeatureFlag("feature_freelancer_projects");
  const bank = useFeatureFlag("feature_freelancer_bank");
  return projects && bank;
}

async function fetchHistory(args: { p_freelancer_id?: string; p_project_id?: string }): Promise<FreelancerProjectHistoryRow[]> {
  const { data, error } = await supabase.rpc("freelancer_project_history", args);
  if (error) throw error;
  return ((data ?? []) as FreelancerProjectHistoryRow[]).map((r) => ({
    ...r,
    invoiced: Number(r.invoiced ?? 0),
    lines: r.lines ?? [],
  }));
}

/** Prosjektene én frilanser er brukt på (profilen). */
export function useFreelancerProjectHistory(freelancerId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["freelancer-project-history", freelancerId],
    enabled: enabled && !!freelancerId,
    queryFn: () => fetchHistory({ p_freelancer_id: freelancerId! }),
  });
}

/** Frilanserne på ett prosjekt (Team & eierskap). */
export function useProjectFreelancers(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["project-freelancers", projectId],
    enabled: enabled && !!projectId,
    queryFn: () => fetchHistory({ p_project_id: projectId! }),
  });
}

/** Hele historikken, for «Sist brukt» i lista. Aggregat, derfor 5 min staleTime. */
export function useFreelancerLastUsedHistory(enabled = true) {
  return useQuery({
    queryKey: ["freelancer-last-used"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchHistory({}),
  });
}
